/* eslint-disable-next-line no-restricted-imports */
import * as t from 'io-ts';

/* eslint-disable-next-line no-restricted-imports */
import type { MenuProduct } from '@jane/shared/models';

import { tApplicabilityRules } from './schemas';
import { tSchedule, tSpecialRulesV2 } from './specialV2';

// Inlined while bloom.ts uses zod and this file still uses io-ts — the two don't
// compose. Remove when this file migrates to zod and import from bloom.ts.
const tBloomFontFamily = t.type({
  url: t.union([t.string, t.undefined]),
  font_family: t.union([t.string, t.undefined]),
});

export type MenuProductToPreview = MenuProduct & {
  applicable_bundle_special_ids: {
    discount: number[];
    qualify: number[];
  } | null;
  inventory_count: number | null;
  kind_subtype: string | null;
  subcategory: string | null;
};

const tEmbeddedMenuRow = t.intersection([
  t.type({
    id: t.number,
    fonts: t.type({
      body: tBloomFontFamily,
      heading: tBloomFontFamily,
    }),
    colors: t.type({
      text: t.string,
      price: t.string,
    }),
    header: t.string,
    updated_at: t.string,
  }),
  t.partial({
    bloom_operator_config_id: t.number,
  }),
]);

const tMenuRowOptional = t.partial({
  applicability_rules: t.array(tApplicabilityRules),
  rules: tSpecialRulesV2,
  custom_display_name: t.union([t.string, t.null]),
  embedded_menu_row: t.union([tEmbeddedMenuRow, t.null]),
  enabled: t.boolean,
  id: t.number,
  is_category: t.boolean,
  menu_products_ids: t.type({
    all: t.array(t.number),
    in_stock: t.array(t.number),
  }),
  // TODO: Should probably be tMenuProduct type, but there are io-ts validation errors
  menu_products_to_preview: t.array(t.any),
  roots_only: t.boolean,
  schedule: tSchedule,
  start_time: t.union([t.string, t.null, t.undefined]),
  end_time: t.union([t.string, t.null, t.undefined]),
  store_id: t.number,
});

const tMenuRowRequired = t.type({
  ranking: t.number,
  row_type: t.union([t.string, t.null]),
  menu_row_type: t.union([t.string, t.null]),
});

export const tMenuRow = t.intersection([tMenuRowOptional, tMenuRowRequired]);
export type MenuRow = t.TypeOf<typeof tMenuRow>;

const t_MenuTab = t.type({
  enabled: t.boolean,
  id: t.number,
  menu_product_count: t.number,
  rank: t.number,
  store_id: t.number,
  tab_name: t.string,
});

export type MenuTab = t.TypeOf<typeof t_MenuTab>;

const tCustomizableType = t.union([
  t.literal('ProductType'),
  t.literal('Lineage'),
]);

const tCustomLabelRequired = t.type({
  customizable_type: tCustomizableType,
  customizable_id: t.number,
  custom_label: t.string,
});

const tCustomLabel = t.intersection([
  t.partial({
    id: t.number,
    product_type: t.union([t.string, t.undefined]),
    product_subtype: t.union([t.string, t.undefined]),
  }),
  tCustomLabelRequired,
]);

export type CustomLabel = t.TypeOf<typeof tCustomLabel>;

const tReservationModeLabelRequired = t.type({
  pickup_label: t.string,
  delivery_label: t.string,
  curbside_label: t.string,
});

const tReservationLabels = t.intersection([
  t.partial({ id: t.union([t.number, t.undefined]) }),
  tReservationModeLabelRequired,
]);

export type ReservationModeLabels = t.TypeOf<typeof tReservationLabels>;

const tDisabledFeelingsRequired = t.type({ tag_id: t.number });
const tDisabledFeelings = t.intersection([
  t.partial({ id: t.number }),
  tDisabledFeelingsRequired,
]);

export type DisabledFeelings = t.TypeOf<typeof tDisabledFeelings>;

const tComplianceLanguageSettings = t.intersection([
  t.type({
    specials: t.string,
    bundle: t.string,
    sales_badge: t.string,
    medical_id: t.string,
    med_card_front: t.string,
    med_card_back: t.string,
  }),
  // Deal labels are only sent when the c1-extended-deals-labels flag is enabled
  t.partial({
    product: t.string,
    bulk_pricing: t.string,
    cart_total: t.string,
  }),
]);
export type ComplianceLanguageSettings = t.TypeOf<
  typeof tComplianceLanguageSettings
>;

const t_FiltersAndLabels = t.type({
  compliance_language_settings_labels: tComplianceLanguageSettings,
  reservation_mode_labels: tReservationLabels,
  custom_labels: t.array(tCustomLabel),
  disabled_feeling_and_activity_tags: t.array(tDisabledFeelings),
});

export type FiltersAndLabels = t.TypeOf<typeof t_FiltersAndLabels>;

const t_CommsBanner = t.partial({
  alt_text: t.string,
  banner_type: t.union([t.literal('image'), t.literal('text')]),
  button_fill_color: t.string,
  button_link: t.string,
  button_text: t.string,
  button_text_color: t.string,
  created_at: t.string,
  enabled: t.boolean,
  header_text_color: t.string,
  id: t.number,
  image: t.string,
  image_opacity: t.number,
  is_expanded: t.boolean,
  message: t.string,
  store_id: t.number,
  title: t.string,
  updated_at: t.string,
});

export type CommunicationBanner = t.TypeOf<typeof t_CommsBanner>;

const tViewDefaultType = t.union([
  t.literal('grid_view'),
  t.literal('list_view'),
]);

// TODO: these will eventually be stripped from the payload.
const tAppearanceOptionalFields = t.partial({
  white_label: t.boolean,
  menu_filter_setting: t.type({
    id: t.number,
    store_id: t.number,
    collapse_desktop: t.boolean,
  }),
});

const tAppearanceRequiredField = t.type({
  white_label_theme_color: t.string,
  white_label_nav_color: t.string,
  view_default: tViewDefaultType,
  pdp_hide_description: t.boolean,
  pdp_hide_effects: t.boolean,
});

const t_Appearance = t.intersection([
  tAppearanceRequiredField,
  tAppearanceOptionalFields,
]);

export type Appearance = t.TypeOf<typeof t_Appearance>;

const t_CarouselBanner = t.intersection([
  t.type({
    store_id: t.number,
    breakpoint: t.union([t.literal('desktop'), t.literal('mobile')]),
    position: t.number,
    visible: t.boolean,
  }),
  t.partial({
    id: t.number,
    file: t.string,
    image_url: t.string,
    alt_text: t.string,
    url: t.string,
  }),
]);

export type CarouselBanner = t.TypeOf<typeof t_CarouselBanner>;
